import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import { Route, Switch, useLocation, useSearchParams } from "wouter";
// Import layout components
import { PropertyEditModal } from "@/components";
import Modal from "@/components/Modal";
import UserProfileForm from "@/components/UserProfileForm";
import { appShell, authUserAtom, selectedAccountUserIdAtom, selectedPropertyIdAtom, updateProfileAtom } from "@/store";
import {
  fetchCurrentIrrigationPlanStatesAtom,
  fetchCurrentLICPacketsAtom,
  fetchCurrentProjectStatesAtom,
  fetchCurrentReservoirStatesAtom,
  fetchLatestLICEventsAtom,
  fetchLatestMeshDeviceEventsAtom,
  fetchLicStatesAtom,
} from "@/store/data";
import BottomTabNavigation from "./components/BottomTabNavigation";
import CodecConfigModal from "./components/CodecConfigModal";
import Header, { type MenuItemType } from "./components/Header";
import { ReservoirForm } from "./components/ReservoirForm";
// Import page components
import Settings from "./components/Settings";
import { TankForm } from "./components/TankForm";
import DashboardPage from "./DashboardPage";
import HardwarePage from "./HardwarePage";
import IrrigationPlanPage from "./IrrigationPlanPage";
import IrrigationPlansPage from "./IrrigationPlansPage";
import ProjectConfigPage from "./ProjectConfigPage";
import ProjectDetailPage from "./ProjectDetailPage";
import ProjectsPage from "./ProjectsPage";
import ReportsPage from "./ReportsPage";
import { ReservoirsPage } from "./ReservoirsPage";
import CodecTimelinePage from "./reports/codec-timeline/CodecTimelinePage";
import IrrigationPlanExecutionReportPage from "./reports/IrrigationPlanExecutionReportPage";
import IrrigationPlanHistoryPage from "./reports/IrrigationPlanHistoryPage";
import { TanksPage } from "./TanksPage";
import WaterPumpPage from "./WaterPumpsPage";

// Main AppShell component
function AppShell() {
  const [location, setLocation] = useLocation();
  const [searchParams, _] = useSearchParams();
  // const searchParams = { get: (key: string) => "" };
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showPropertyEditModal, setShowPropertyEditModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showCodecConfigModal, setShowCodecConfigModal] = useState(false);
  const selectedAccountUserId = useAtomValue(selectedAccountUserIdAtom);
  const selectedPropertyId = useAtomValue(selectedPropertyIdAtom);
  const currentUser = useAtomValue(authUserAtom);
  const updateProfile = useSetAtom(updateProfileAtom);
  const [backButton, setBackButton] = useAtom(appShell.backButtonAtom);
  const mainRef = useRef<HTMLElement>(null);
  // Start fetching current project states. Although it's not used in this page, it's needed to start the polling
  const fetchCurrentProjectStates = useAtomValue(fetchCurrentProjectStatesAtom);
  // Start fetching current irrigation plan states. Although it's not used in this page, it's needed to start the polling
  const fetchCurrentIrrigationPlanStates = useAtomValue(fetchCurrentIrrigationPlanStatesAtom);
  const startReservoirPolling = useAtomValue(fetchCurrentReservoirStatesAtom);
  // Start fetching current LIC packets. Although it's not used in this page, it's needed to start the polling
  const fetchCurrentLICPackets = useAtomValue(fetchCurrentLICPacketsAtom);
  // Start fetching current LIC events. Although it's not used in this page, it's needed to start the polling
  const fetchLatestLICEvents = useAtomValue(fetchLatestLICEventsAtom);
  // Start fetching current mesh device events. Although it's not used in this page, it's needed to start the polling
  const fetchLatestMeshDeviceEvents = useAtomValue(fetchLatestMeshDeviceEventsAtom);
  // Start fetching LIC states. Although it's not used in this page, it's needed to start the polling
  const fetchLicStates = useAtomValue(fetchLicStatesAtom);

  // Define the root tab paths that should show bottom navigation
  const tabPaths = [
    "/app/dashboard",
    "/app/projects",
    "/app/devices",
    "/app/reports",
    "/app/reports/irrigation-plan-history",
    "/app/reports/irrigation-plan-execution-report",
    "/app/irrigation-plans",
    "/app/pumps",
    "/app/reservoirs",
    "/app/tanks",
  ];
  const shouldShowBottomNav = tabPaths.some((path) => {
    if (location === path) {
      return true;
    }
    return location.startsWith(`${path}/`) || location.startsWith(`${path}?`);
  });

  useEffect(() => {
    // Scroll main content to top on location change
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    console.log("AppShell - location changed: ", location);
  }, [location]);

  useEffect(() => {
    // Reset back button state when location changes
    console.log("AppShell - back button state: ", backButton);
  }, [backButton]);

  useEffect(() => {
    // Route guards - check authentication and selections
    if (!selectedAccountUserId) {
      console.warn("AppShell - No account selected, redirecting to account selection");
      setLocation("/select-account");
      return;
    }

    if (!selectedPropertyId) {
      console.warn("AppShell - No property selected, redirecting to property selection");
      setLocation("/select-property");
      return;
    }
    // Profile completion guard
    if (currentUser && (!currentUser.cpf || !currentUser.phone_number)) {
      setShowProfileModal(true);
    } else {
      setShowProfileModal(false);
    }
  }, [setLocation, location, selectedAccountUserId, selectedPropertyId, currentUser]);

  function handleMenuItemClick(item: MenuItemType) {
    if (item === "profile") {
      setShowSettingsModal(true);
      // Redirect to previous page or dashboard
    } else if (item === "projects") {
      setLocation("/app/projects");
    } else if (item === "equipment") {
      setLocation("/app/devices");
    } else if (item === "reports") {
      setLocation("/app/reports");
    } else if (item === "reservoirs") {
      setLocation("/app/reservoirs");
    } else if (item === "tanks") {
      setLocation("/app/tanks");
    } else if (item === "property") {
      setShowPropertyEditModal(true);
    } else if (item === "codec_config") {
      setShowCodecConfigModal(true);
    } else {
      console.warn(`Unhandled menu item: ${item}`);
    }
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <Header showSettingsModal={showSettingsModal} onMenuClicked={handleMenuItemClick} backButton={backButton} />
      <main ref={mainRef} className="flex-1 overflow-auto">
        <Switch>
          <Route path="/app/dashboard" component={DashboardPage} />
          <Route path="/app/devices" component={(props) => <HardwarePage {...props} activeTab={"devices"} />} />
          <Route path="/app/projects/new">
            {(props) => <ProjectConfigPage {...props} projectId="new" activeTab={"config"} />}
          </Route>
          <Route
            path="/app/projects/:projectId/config"
            component={(props) => <ProjectConfigPage {...props.params} activeTab={"config"} />}
          />
          <Route
            path="/app/projects/:projectId/irrigation-plans/new"
            component={(props) => {
              const projectId = props.params.projectId;
              setLocation(`/app/irrigation-plans/new?projectId=${projectId}`);
              return null;
            }}
          />
          <Route
            path="/app/projects/:projectId/irrigation-plans/:planId"
            component={(props) => <IrrigationPlanPage {...props} planId={props.params.planId} activeTab={"config"} />}
          />
          <Route path="/app/projects/:projectId">{(props) => <ProjectDetailPage {...props} />}</Route>
          <Route path="/app/projects" component={ProjectsPage} />
          <Route path="/app/irrigation-plans" component={IrrigationPlansPage} />
          <Route path="/app/irrigation-plans/new">
            {(props) => {
              const projectId = searchParams.get("projectId");
              if (!projectId) {
                // If no projectId provided, redirect to irrigation plans list
                console.warn("No projectId provided in irrigation plan creation route");
                setLocation("/app/irrigation-plans");
                return null;
              }
              return <IrrigationPlanPage {...props} planId="new" activeTab={"config"} />;
            }}
          </Route>
          <Route path="/app/irrigation-plans/:planId">
            {(props) => {
              return <IrrigationPlanPage {...props} activeTab={"config"} />;
            }}
          </Route>
          <Route path="/app/pumps" component={WaterPumpPage} />
          <Route path="/app/reservoirs" component={ReservoirsPage} />
          <Route path="/app/reservoirs/new" component={ReservoirForm} />
          <Route path="/app/reservoirs/:id" component={ReservoirForm} />
          <Route path="/app/tanks" component={TanksPage} />
          <Route path="/app/tanks/new" component={TankForm} />
          <Route path="/app/tanks/:id" component={TankForm} />
          <Route path="/app/reports/irrigation-plan-history" component={IrrigationPlanHistoryPage} />
          <Route path="/app/reports/irrigation-plan-execution-report" component={IrrigationPlanExecutionReportPage} />
          <Route path="/app/reports/codec-timeline" component={CodecTimelinePage} />
          <Route path="/app/reports" component={ReportsPage} />
          {/* Default route within app - redirect to dashboard */}
          <Route
            path="/app"
            component={() => {
              setLocation("/app/dashboard");
              return null;
            }}
          />
        </Switch>
      </main>
      {shouldShowBottomNav && <BottomTabNavigation />}

      {/* Settings Modal */}
      <Modal isOpen={showSettingsModal} onClose={() => setShowSettingsModal(false)} title="Configurações" size="xl">
        <Settings />
      </Modal>

      {/* Profile Completion Modal (blocking) */}
      <Modal
        isOpen={showProfileModal}
        onClose={() => {
          // Keep blocking until completed
          setShowProfileModal(true);
        }}
        closable={false}
        title="Complete seu perfil"
        size="md"
      >
        <div className="space-y-3">
          <p className="text-sm text-neutral-700">Informe seu CPF e telefone para continuar usando o app.</p>
          <UserProfileForm
            initialValues={{
              first_name: currentUser?.first_name ?? undefined,
              last_name: currentUser?.last_name ?? undefined,
              cpf: currentUser?.cpf ?? "",
              phone_number: currentUser?.phone_number ?? "",
            }}
            onSubmit={async (data) => {
              const result = await updateProfile(data);
              if (result?.success) {
                setShowProfileModal(false);
              }
            }}
            showNameFields={true}
            email={currentUser?.email ?? null}
            submitLabel="Salvar e continuar"
          />
        </div>
      </Modal>

      {/* Property Edit Modal */}
      <PropertyEditModal isOpen={showPropertyEditModal} onClose={() => setShowPropertyEditModal(false)} />

      {/* Codec Config Modal */}
      <CodecConfigModal isOpen={showCodecConfigModal} onClose={() => setShowCodecConfigModal(false)} />
    </div>
  );
}

export default AppShell;
